import { useState } from "react";
import { Button } from "@/components/ui/button";
import { ArrowRight, ExternalLink, Award } from "lucide-react";
import { SimState, fmt } from "@/hooks/useREInvestingSim";
import { supabase } from "@/integrations/supabase/client";
import ModuleFeedbackCard from "@/components/ai/ModuleFeedbackCard";
import RecapPanel from "./RecapPanel";
import type { REDecisionEntry } from "@/hooks/useREDecisionLog";
import { getRealm } from "@/components/lunimar/lunimarRealms";
import {
  MissionResultPanel,
  ReadinessMeter,
  ModuleRecapCard,
  SimWalletImpact,
  LumiSignalCard,
  ParentInsightSummary,
  RiskMeter,
  type RiskLevel,
} from "@/components/lunimar-kit";
import { useWallet } from "@/hooks/useWallet";
import type { AnyRow } from "@/lib/rowTypes";

interface Props {
  sim: SimState;
  netWorth: number;
  totalPortfolioValue: number;
  totalMortgageDebt: number;
  monthlyCashFlow: number;
  onFinish: () => void;
  onDashboard: () => void;
  recap?: {
    totalWallet: number;
    totalScore: number;
    readiness: string;
    strongest: REDecisionEntry | null;
    weakest: REDecisionEntry | null;
    tradeoff: string;
    takeaway: string;
    nextAction: string;
  } | null;
  decisionLog?: REDecisionEntry[];
}

const ACHIEVEMENT_LABELS: Record<string, { label: string; emoji: string }> = {
  first_deal: { label: "First Deal", emoji: "🔑" },
  cash_flow_starter: { label: "Cash Flow Starter", emoji: "💵" },
  reserve_protector: { label: "Reserve Protector", emoji: "🛡️" },
  tenant_keeper: { label: "Tenant Keeper", emoji: "👤" },
  fixer_visionary: { label: "Fixer Visionary", emoji: "🔧" },
  smart_financer: { label: "Smart Financer", emoji: "📊" },
  vacancy_survivor: { label: "Vacancy Survivor", emoji: "🏗️" },
  market_cycle_master: { label: "Market Cycle Master", emoji: "🏆" },
  portfolio_builder: { label: "Portfolio Builder", emoji: "🏠" },
  long_term_thinker: { label: "Long-Term Thinker", emoji: "📈" },
  balanced_investor: { label: "Balanced Investor", emoji: "⚖️" },
  risk_manager: { label: "Risk Manager", emoji: "🎓" },
};

function analyzeDecisions(metrics: Record<string, AnyRow>, sim: SimState) {
  const decisions = metrics?.decisions || [];
  let strongest = "Making your first investment";
  let weakest = "No major mistakes identified";
  let narrative = "";

  // Analyze strongest
  const buys = decisions.filter((d: AnyRow) => d.type === "buy");
  const repairs = decisions.filter((d: AnyRow) => d.type === "repair" || d.type === "renovate");
  const sells = decisions.filter((d: AnyRow) => d.type === "sell");

  if (buys.length > 0 && sim.portfolio.length > 0) {
    const avgAppreciation = sim.portfolio.reduce((s, p) => s + (p.current_value - p.purchase_price), 0) / sim.portfolio.length;
    if (avgAppreciation > 0) strongest = "Your property selections appreciated well";
  }
  if (repairs.length >= 3) strongest = "Consistent property maintenance kept your portfolio healthy";
  if (sim.achievements.includes("balanced_investor")) strongest = "Maintaining both positive cash flow and healthy reserves";
  if (sim.achievements.includes("market_cycle_master")) strongest = "Successfully navigating multiple market cycles";

  // Analyze weakest
  if (sim.available_cash < sim.starting_capital * 0.1) weakest = "Low cash reserves — consider keeping more liquidity";
  if (sim.total_repair_costs > sim.total_rental_income * 0.3) weakest = "High repair costs ate into your returns";
  const vacantProps = sim.portfolio.filter(p => !p.occupied);
  if (vacantProps.length > 0) weakest = "Vacancy issues reduced your income potential";
  if (sim.portfolio.some(p => p.condition_score < 30)) weakest = "Some properties deteriorated from deferred maintenance";

  // Narrative
  const profit = (sim.available_cash + sim.portfolio.reduce((s, p) => s + p.current_value - p.mortgage_balance, 0)) - sim.starting_capital;
  if (profit > sim.starting_capital * 0.3) {
    narrative = "Excellent work! You built a strong portfolio that balanced income and growth. Your analytical approach and risk management created real wealth through real estate.";
  } else if (profit > 0) {
    narrative = "You built a portfolio that generated positive returns while navigating the challenges of property ownership. Real estate rewards patience and smart decision-making.";
  } else {
    narrative = "Your portfolio faced challenges, but the experience taught valuable lessons. Remember: careful analysis, healthy reserves, and patience are key to long-term real estate success.";
  }

  return { strongest, weakest, narrative };
}

export default function SummaryScreen({ sim, netWorth, totalPortfolioValue, totalMortgageDebt, monthlyCashFlow, onFinish, onDashboard, recap, decisionLog = [] }: Props) {
  const realm = getRealm("re_investing");
  const wallet = useWallet();
  const [completing, setCompleting] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [walletReward, setWalletReward] = useState<number | null>(null);
  const profit = netWorth - sim.starting_capital;
  const profitPct = ((profit / sim.starting_capital) * 100).toFixed(1);
  const profitRatio = profit / Math.max(1, sim.starting_capital);
  const analysis = analyzeDecisions(sim.metrics || {}, sim);

  /* Readiness — derived from profit, reserves, and cash flow. */
  const readiness = (() => {
    let v = 50;
    if (profitRatio > 0.3) v += 25;
    else if (profitRatio > 0) v += 12;
    else if (profitRatio < -0.15) v -= 20;
    if (monthlyCashFlow > 500) v += 15;
    else if (monthlyCashFlow < 0) v -= 15;
    if (sim.available_cash > sim.starting_capital * 0.15) v += 10;
    if (sim.portfolio.some(p => p.condition_score < 30)) v -= 8;
    return Math.max(0, Math.min(100, v));
  })();

  const outcomeLabel =
    readiness >= 80 ? "Capital Strategist" :
    readiness >= 60 ? "Steady Operator" :
    readiness >= 40 ? "Learning Investor" :
    "Overleveraged Risk";

  /* Risk level — leverage ratio + reserves. */
  const leverage = totalPortfolioValue > 0 ? totalMortgageDebt / totalPortfolioValue : 0;
  const risk: RiskLevel =
    leverage > 0.85 || sim.available_cash < sim.starting_capital * 0.05 ? "Severe" :
    leverage > 0.7 ? "High" :
    leverage > 0.5 ? "Medium" : "Low";

  /* Persistent cash is catalogued server-side ($30 simulator tier); the simulated
     portfolio value never transfers into the learner's real balance. */


  const handleComplete = async () => {
    setCompleting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      await supabase.functions.invoke("re-investing-engine", {
        body: { action: "complete", sim_id: sim.id },
        headers: { Authorization: `Bearer ${session.access_token}` },
      });

      /* Catalogued, one-time module cash — never the simulated portfolio value. */
      try {
        await supabase.rpc("record_module_completion" as never, {
          p_module_key: "re_investing", p_score: 1, p_max_score: 1,
        } as never);
        const { data: award } = await supabase.rpc("award_module_completion_reward" as never, {
          p_module_key: "re_investing",
        } as never);
        setWalletReward(Number((award as { amount?: number } | null)?.amount ?? 0));
      } catch (err) {
        console.warn("[RE Investing] wallet reward failed:", err);
      }


      setCompleted(true);
    } catch (e) {
      console.error("Failed to complete module:", e);
    } finally {
      setCompleting(false);
    }
  };

  return (
    <div className="mx-auto w-full max-w-2xl space-y-5 py-4 sm:py-6">
      <MissionResultPanel
        realm={realm}
        eyebrow="Skyline Holdings · Mission Debrief"
        title={`${sim.current_round}-month operating run complete`}
        outcome={outcomeLabel}
      >
        {/* Readiness + risk side by side */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="rounded-xl border border-white/10 bg-white/5 p-3">
            <ReadinessMeter
              realm={realm}
              value={readiness}
              label="Investor readiness"
              outcome={outcomeLabel}
            />
          </div>
          <RiskMeter
            level={risk}
            label="Portfolio leverage risk"
            hint={`Debt covers ${(leverage * 100).toFixed(0)}% of portfolio value.`}
          />
        </div>

        {/* Net result wallet impact (simulated portfolio) */}
        <SimWalletImpact
          realm={realm}
          delta={profit}
          balanceAfter={netWorth}
          label="Simulated portfolio result"
          persisted={false}
        />

        {/* Real wallet completion reward */}
        {walletReward != null && (
          <SimWalletImpact
            realm={realm}
            delta={walletReward}
            label="HeirWise wallet · completion reward"
            persisted
          />
        )}

        {/* Final operating metrics */}
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          {[
            ["Starting", fmt(sim.starting_capital)],
            ["Net worth", fmt(netWorth)],
            ["Return", `${profit >= 0 ? "+" : ""}${profitPct}%`],
            ["Properties", String(sim.portfolio.length)],
            ["Portfolio value", fmt(totalPortfolioValue)],
            ["Total debt", fmt(totalMortgageDebt)],
            ["Monthly cash flow", fmt(monthlyCashFlow)],
            ["Rental income", fmt(sim.total_rental_income)],
            ["Repair costs", fmt(sim.total_repair_costs)],
          ].map(([label, value]) => (
            <div key={label} className="rounded-lg border border-white/10 bg-white/5 p-2.5">
              <p className="text-[10px] font-mono uppercase tracking-widest text-white/55">{label}</p>
              <p className="mt-0.5 font-heading text-sm font-bold tabular-nums text-white">{value}</p>
            </div>
          ))}
        </div>

        {/* Strongest / weakest decisions */}
        <ModuleRecapCard
          realm={realm}
          strongest={{ label: analysis.strongest, detail: undefined }}
          weakest={{ label: analysis.weakest, detail: undefined }}
          tradeoffLearned={`Real estate forces a constant choice between cash flow (income now) and appreciation (value later). Your portfolio leaned ${monthlyCashFlow >= 0 ? "toward steady cash flow" : "toward growth bets without enough buffer"}.`}
          realWorldTakeaway={analysis.narrative}
        />

        {/* Achievements */}
        {sim.achievements.length > 0 && (
          <div
            className="rounded-xl border p-3"
            style={{
              borderColor: `hsl(${realm.tertiaryAccent} / 0.35)`,
              background: `hsl(${realm.tertiaryAccent} / 0.08)`,
            }}
          >
            <div
              className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest"
              style={{ color: `hsl(${realm.tertiaryAccent})` }}
            >
              <Award className="h-3 w-3" /> Achievements unlocked · {sim.achievements.length}/12
            </div>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {sim.achievements.map(a => {
                const info = ACHIEVEMENT_LABELS[a];
                return (
                  <span
                    key={a}
                    className="inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider"
                    style={{
                      borderColor: `hsl(${realm.tertiaryAccent} / 0.4)`,
                      color: `hsl(${realm.tertiaryAccent})`,
                    }}
                  >
                    <span>{info?.emoji || "🏅"}</span> {info?.label || a}
                  </span>
                );
              })}
            </div>
          </div>
        )}

        {/* Lumi signal */}
        <LumiSignalCard realm={realm} tag={readiness >= 60 ? "Confirmed" : "Watch"}>
          {readiness >= 60
            ? `Strong operating discipline. You kept ${fmt(sim.available_cash)} in reserves while running ${sim.portfolio.length} properties.`
            : `In future runs, prioritize reserves and positive cash flow before adding leverage. Leverage at ${(leverage * 100).toFixed(0)}% leaves little room for a vacancy or repair shock.`}
        </LumiSignalCard>

        {/* Parent prompts */}
        <ParentInsightSummary
          realm={realm}
          prompts={[
            "Why do real estate investors care about cash flow as much as appreciation?",
            `What would you do differently next time about leverage? Yours hit ${(leverage * 100).toFixed(0)}%.`,
            "Name one expense that surprised you in this run.",
          ]}
        />

        {/* Lunimar full recap panel (existing decision log) */}
        {recap && (
          <div className="space-y-2">
            <div className="text-[10px] font-mono uppercase tracking-widest text-white/55">
              Decision log
            </div>
            <RecapPanel recap={recap} log={decisionLog} />
          </div>
        )}

        <ModuleFeedbackCard
          moduleName="Real Estate Investing Simulator"
          moduleKey="re_investing_sim"
          finalMetrics={{
            starting_capital: sim.starting_capital,
            net_worth: netWorth,
            portfolio_value: totalPortfolioValue,
            mortgage_debt: totalMortgageDebt,
            monthly_cash_flow: monthlyCashFlow,
            properties_owned: sim.portfolio.length,
            total_rental_income: sim.total_rental_income,
            total_repair_costs: sim.total_repair_costs,
          }}
          outcome={profit >= 0 ? "profit" : "loss"}
        />

        {/* Persistence + actions */}
        {!completed ? (
          <div className="space-y-2">
            <Button onClick={handleComplete} disabled={completing} className="w-full gap-2">
              {completing ? "Closing the books…" : "Save run · Claim wallet reward"}
              <ExternalLink className="h-4 w-4" />
            </Button>
            <p className="text-center text-[10px] text-white/55">
              Properties and mortgages are added to your HeirWise financial profile.
            </p>
          </div>
        ) : (
          <div
            className="rounded-xl border p-3 text-center"
            style={{
              borderColor: "hsl(152 70% 55% / 0.4)",
              background: "hsl(152 70% 32% / 0.12)",
              color: "hsl(152 70% 65%)",
            }}
          >
            <p className="font-mono text-xs font-semibold uppercase tracking-wider">
              Saved to your portfolio
              {walletReward != null && ` · +${fmt(walletReward)} added to wallet`}
            </p>
          </div>
        )}

        <div className="flex flex-col gap-2 sm:flex-row">
          <Button variant="outline" onClick={onDashboard} className="flex-1">
            Return to dashboard
          </Button>
          <Button variant={completed ? "default" : "outline"} onClick={onFinish} className="flex-1 gap-2">
            Complete module <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      </MissionResultPanel>
    </div>
  );
}
